import React, { useState, useEffect } from 'react'
import {
  Modal,
  ModalHeader,
  ModalContent,
  ModalFooter,
  Button,
  TextField,
  Checkbox,
  Text,
  Flex,
  Box,
  Loader,
} from '@vibe/core'
import { Dropdown } from '@vibe/core/next'
import { Close, Show, Hide } from '@vibe/icons'
import type {
  UserInfo,
  CreateUserRequest,
  UpdateUserRequest,
  UserRole,
  RoleInfo,
} from '../../types'
import { UserRole as UserRoleEnum } from '../../types'
import { apiService } from '../../services/api'
import { isValidEmail, isValidName } from '../../utils/userManagementValidation'
import { getErrorMessage } from '../../utils/errorHandling'

interface FormErrors {
  [key: string]: string
}

interface UserFormProps {
  user?: UserInfo | null
  onCreateUser?: (userData: CreateUserRequest) => Promise<void>
  onUpdateUser?: (userId: string, userData: UpdateUserRequest) => Promise<void>
  onClose: () => void
  isOpen: boolean
}

/**
 * User form modal for creating and editing users
 */
export const UserForm: React.FC<UserFormProps> = ({
  user,
  onCreateUser,
  onUpdateUser,
  onClose,
  isOpen,
}) => {
  const isEditing = !!user

  // Form state
  const [formData, setFormData] = useState({
    email: user?.email || '',
    name: user?.name || '',
    role: user?.role || UserRoleEnum.VISUALIZADOR,
    temporary_password: '',
  })

  const [errors, setErrors] = useState<FormErrors>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [generatePassword, setGeneratePassword] = useState(true) // Start with auto-generation enabled
  const [roles, setRoles] = useState<RoleInfo[]>([])
  const [loadingRoles, setLoadingRoles] = useState(true)

  /**
   * Load available roles
   */
  useEffect(() => {
    const loadRoles = async () => {
      try {
        setLoadingRoles(true)
        const response = await apiService.getRoles()
        setRoles(response.data.items)
      } catch (err) {
        console.error('Error loading roles:', err)
        // Use default roles if API fails
        setRoles([
          {
            id: '1',
            name: UserRoleEnum.ADMINISTRADOR,
            description: 'Acceso completo al sistema',
            permissions: [],
            is_system_role: true,
            user_count: 0,
            created_at: '',
            updated_at: '',
            cognito_group_name: UserRoleEnum.ADMINISTRADOR,
          },
          {
            id: '2',
            name: UserRoleEnum.EDITOR,
            description: 'Puede crear y gestionar solicitudes',
            permissions: [],
            is_system_role: true,
            user_count: 0,
            created_at: '',
            updated_at: '',
            cognito_group_name: UserRoleEnum.EDITOR,
          },
          {
            id: '3',
            name: UserRoleEnum.VISUALIZADOR,
            description: 'Solo lectura',
            permissions: [],
            is_system_role: true,
            user_count: 0,
            created_at: '',
            updated_at: '',
            cognito_group_name: UserRoleEnum.VISUALIZADOR,
          },
        ])
      } finally {
        setLoadingRoles(false)
      }
    }

    if (isOpen) {
      loadRoles()
    }
  }, [isOpen])

  /**
   * Generate random password
   */
  const generateRandomPassword = () => {
    const length = 16
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
    const lowercase = 'abcdefghijklmnopqrstuvwxyz'
    const numbers = '0123456789'
    const specialChars = '!@#$%^&*()_+-=[]{}|;:,.<>?'

    // Ensure at least one character from each required category
    let password = ''
    password += uppercase.charAt(Math.floor(Math.random() * uppercase.length))
    password += lowercase.charAt(Math.floor(Math.random() * lowercase.length))
    password += numbers.charAt(Math.floor(Math.random() * numbers.length))
    password += specialChars.charAt(
      Math.floor(Math.random() * specialChars.length)
    )

    // Fill the rest with random characters from all categories
    const allChars = uppercase + lowercase + numbers + specialChars
    for (let i = 4; i < length; i++) {
      password += allChars.charAt(Math.floor(Math.random() * allChars.length))
    }

    // Shuffle the password to randomize character positions
    return password
      .split('')
      .sort(() => Math.random() - 0.5)
      .join('')
  }

  /**
   * Handle generate password
   */
  const handleGeneratePassword = () => {
    const newPassword = generateRandomPassword()
    setFormData(prev => ({ ...prev, temporary_password: newPassword }))
    setGeneratePassword(false) // Set to false so the password gets sent to backend
  }

  /**
   * Validate form data
   */
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'El email es requerido'
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = 'El email no es válido'
    }

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es requerido'
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'El nombre debe tener al menos 2 caracteres'
    } else if (formData.name.trim().length > 100) {
      newErrors.name = 'El nombre no puede tener más de 100 caracteres'
    } else if (
      !/^[a-zA-ZÀ-ÿ\u00f1\u00d1\s\-'\.]+$/.test(formData.name.trim())
    ) {
      newErrors.name =
        'El nombre solo puede contener letras, espacios, guiones y apostrofes'
    }

    // Role validation
    if (!formData.role) {
      newErrors.role = 'El rol es requerido'
    }

    // Password validation for new users
    if (
      !isEditing &&
      !generatePassword &&
      !formData.temporary_password.trim()
    ) {
      newErrors.temporary_password = 'La contraseña temporal es requerida'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    try {
      setIsSubmitting(true)

      if (isEditing && user && onUpdateUser) {
        // Update existing user
        const updateData: UpdateUserRequest = {
          name: formData.name,
          role: formData.role,
        }

        await onUpdateUser(user.id, updateData)
      } else if (!isEditing && onCreateUser) {
        // Create new user
        const createData: CreateUserRequest = {
          email: formData.email,
          name: formData.name,
          role: formData.role,
        }

        // Add password if we have one (either generated or manually entered)
        if (formData.temporary_password && formData.temporary_password.trim()) {
          createData.temporary_password = formData.temporary_password
        }

        await onCreateUser(createData)
      }

      onClose()
    } catch (err) {
      const errorMessage = getErrorMessage(err)

      // Handle specific validation errors
      if (errorMessage.includes('email')) {
        setErrors({ email: errorMessage })
      } else if (errorMessage.includes('role')) {
        setErrors({ role: errorMessage })
      } else {
        setErrors({ general: errorMessage })
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  /**
   * Handle input changes
   */
  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }))

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  /**
   * Handle role change
   */
  const handleRoleChange = (selectedOption: any) => {
    if (selectedOption) {
      setFormData(prev => ({ ...prev, role: selectedOption.value as UserRole }))
      if (errors.role) {
        setErrors(prev => ({ ...prev, role: '' }))
      }
    }
  }

  /**
   * Get role options for dropdown
   */
  const getRoleOptions = () => {
    return roles.map(role => ({
      value: role.name,
      label: `${role.name} - ${role.description}`,
    }))
  }

  /**
   * Get selected role option
   */
  const getSelectedRoleOption = () => {
    const options = getRoleOptions()
    return options.find(option => option.value === formData.role) || undefined
  }

  /**
   * Reset form when modal closes
   */
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        email: user?.email || '',
        name: user?.name || '',
        role: user?.role || UserRoleEnum.VISUALIZADOR,
        temporary_password: '',
      })
      setErrors({})
      setGeneratePassword(true) // Always start with auto-generation enabled
      setShowPassword(false)
    }
  }, [isOpen, user, isEditing])

  return (
    <Modal show={isOpen} onClose={onClose}>
      <ModalHeader>
        <Flex justify="space-between" align="center">
          <Text type="text1" weight="bold">
            {isEditing ? 'Editar Usuario' : 'Crear Usuario'}
          </Text>
          <Button
            kind="tertiary"
            size="small"
            onClick={onClose}
            leftIcon={Close}
            ariaLabel="Cerrar"
          >
            Cerrar
          </Button>
        </Flex>
      </ModalHeader>

      <ModalContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* General Error */}
          {errors.general && (
            <Box className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <Text color="negative">{errors.general}</Text>
            </Box>
          )}

          {/* Email Field */}
          <TextField
            title="Email *"
            placeholder="usuario@empresa.com"
            value={formData.email}
            onChange={value => handleInputChange('email', value)}
            validation={
              errors.email ? { status: 'error', text: errors.email } : undefined
            }
            disabled={isEditing} // Email cannot be changed when editing
          />

          {/* Name Field */}
          <TextField
            title="Nombre Completo *"
            placeholder="Nombre y apellido del usuario"
            value={formData.name}
            onChange={value => handleInputChange('name', value)}
            validation={
              errors.name ? { status: 'error', text: errors.name } : undefined
            }
          />

          {/* Role Selection */}
          <Box>
            <Text type="text2" weight="medium" className="mb-2">
              Rol *
            </Text>
            {loadingRoles ? (
              <Flex
                align="center"
                gap="small"
                className="p-3 border rounded-lg"
              >
                <Loader size="small" />
                <Text>Cargando roles...</Text>
              </Flex>
            ) : (
              <div className="dropdown-overflow-visible">
                <Dropdown
                  placeholder="Seleccionar rol"
                  options={getRoleOptions()}
                  value={getSelectedRoleOption()}
                  onChange={handleRoleChange}
                  error={!!errors.role}
                  helperText={errors.role}
                  required
                  ariaLabel="Seleccionar rol del usuario"
                  clearable={false}
                />
              </div>
            )}
          </Box>

          {/* Password Section (only for new users) */}
          {!isEditing && (
            <Box>
              <Flex justify="space-between" align="center" className="mb-3">
                <Text type="text2" weight="medium">
                  Contraseña Temporal
                </Text>
                <Button
                  kind="tertiary"
                  size="small"
                  onClick={handleGeneratePassword}
                >
                  Generar Contraseña
                </Button>
              </Flex>

              <div className="relative">
                <TextField
                  placeholder="Contraseña temporal para el usuario"
                  value={formData.temporary_password}
                  onChange={value =>
                    handleInputChange('temporary_password', value)
                  }
                  validation={
                    errors.temporary_password
                      ? { status: 'error', text: errors.temporary_password }
                      : undefined
                  }
                  type={showPassword ? 'text' : 'password'}
                  disabled={generatePassword}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={0}
                >
                  {showPassword ? (
                    <Hide className="w-5 h-5" />
                  ) : (
                    <Show className="w-5 h-5" />
                  )}
                </button>
              </div>
            </Box>
          )}
        </form>
      </ModalContent>

      <ModalFooter>
        <Flex justify="end" gap="medium">
          <Button kind="tertiary" onClick={onClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button
            kind="primary"
            onClick={handleSubmit}
            disabled={isSubmitting || loadingRoles}
          >
            {isSubmitting ? (
              <Flex align="center" gap="small">
                <Loader size="small" />
                <Text>{isEditing ? 'Actualizando...' : 'Creando...'}</Text>
              </Flex>
            ) : isEditing ? (
              'Actualizar Usuario'
            ) : (
              'Crear Usuario'
            )}
          </Button>
        </Flex>
      </ModalFooter>
    </Modal>
  )
}
